import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, cn } from "@finster/ui";
import { ArrowLeft, Check } from "lucide-react";
import { getAvailableFinsterQueryDataSourceGroups } from "../api/finster/constants";
import type { FeatureFlags, FinsterQueryDataSource } from "../api/finster/constants";

export type DataSourcesBrowserCloseReason = "dismiss" | "escape" | "back";

export interface DataSourcesBrowserPanelProps {
  open: boolean;
  selectedDataSources: readonly FinsterQueryDataSource[];
  onDataSourcesChange: (dataSources: FinsterQueryDataSource[]) => void;
  featureFlags?: FeatureFlags;
  featureFlagsLoading?: boolean;
  /** Attachment picker menu root — same anchor Skills and Your Files use. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onOpenChange: (open: boolean, reason?: DataSourcesBrowserCloseReason) => void;
}

const PANEL_GAP_PX = 8;
const PANEL_VIEWPORT_PADDING_PX = 16;
const PANEL_MAX_HEIGHT_PX = 440;
const PANEL_MAX_HEIGHT_ABOVE_PX = 380;
const PANEL_WIDTH_PX = 320;

/**
 * Status text for the plus-menu Data Sources row: Off, All, or a count.
 * Matches the rules the standalone chip used before FE-3231.
 */
export function getDataSourcesStatusLabel(
  selectedDataSources: readonly FinsterQueryDataSource[],
  availableDataSources: readonly FinsterQueryDataSource[]
): string {
  if (selectedDataSources.length === 0) return "Off";
  const selectedSet = new Set(selectedDataSources);
  const allSelected =
    availableDataSources.length > 0 &&
    availableDataSources.every((source) => selectedSet.has(source));
  if (allSelected) return "All";
  return String(selectedDataSources.length);
}

/**
 * Secondary panel opened from the plus-menu Data Sources row. Keeps the
 * grouped checklist and Select All / Deselect All behavior from the former
 * chip menu; Back returns to the attachment options list.
 */
export const DataSourcesBrowserPanel: React.FC<DataSourcesBrowserPanelProps> = ({
  open,
  selectedDataSources,
  onDataSourcesChange,
  featureFlags,
  featureFlagsLoading = false,
  anchorRef,
  onOpenChange,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const [panelPosition, setPanelPosition] = useState<{
    side: "top" | "bottom";
    left: number;
    verticalOffset: number;
    maxHeight: number;
    width: number;
  }>({
    side: "top",
    left: 0,
    verticalOffset: 0,
    maxHeight: PANEL_MAX_HEIGHT_PX,
    width: PANEL_WIDTH_PX,
  });

  const availableGroups = getAvailableFinsterQueryDataSourceGroups(
    featureFlags,
    featureFlagsLoading
  );
  const availableDataSources = availableGroups.flatMap((group) =>
    group.children.map((child) => child.param)
  );
  const selectedSourceSet = new Set(selectedDataSources);
  const allSourcesSelected =
    availableDataSources.length > 0 &&
    availableDataSources.every((source) => selectedSourceSet.has(source));

  useEffect(() => {
    if (!open) return undefined;
    // The plus menu unmounts as this dialog opens and does not restore the
    // trigger, so focus has to land inside the panel or Tab never reaches it.
    const timeoutId = window.setTimeout(() => backButtonRef.current?.focus(), 0);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !anchorRef.current?.contains(target)) {
        onOpenChange(false, "dismiss");
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChange(false, "escape");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorRef, onOpenChange, open]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePanelPosition = () => {
      const triggerRect = anchorRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      const width = Math.min(
        PANEL_WIDTH_PX,
        Math.max(0, window.innerWidth - PANEL_VIEWPORT_PADDING_PX * 2)
      );

      const availableAbove = triggerRect.top - PANEL_VIEWPORT_PADDING_PX - PANEL_GAP_PX;
      const availableBelow =
        window.innerHeight - triggerRect.bottom - PANEL_VIEWPORT_PADDING_PX - PANEL_GAP_PX;
      // Composer sits at the bottom of the taskpane — prefer opening upward.
      const side =
        availableAbove >= PANEL_MAX_HEIGHT_ABOVE_PX || availableAbove >= availableBelow
          ? "top"
          : "bottom";
      const availableHeight = side === "top" ? availableAbove : availableBelow;
      const preferredMaxHeight =
        side === "top" ? PANEL_MAX_HEIGHT_ABOVE_PX : PANEL_MAX_HEIGHT_PX;
      const maxHeight = Math.max(0, Math.min(preferredMaxHeight, Math.floor(availableHeight)));
      const maxLeft = Math.max(
        PANEL_VIEWPORT_PADDING_PX,
        window.innerWidth - PANEL_VIEWPORT_PADDING_PX - width
      );

      setPanelPosition({
        side,
        left: Math.min(Math.max(PANEL_VIEWPORT_PADDING_PX, triggerRect.left), maxLeft),
        verticalOffset:
          side === "top"
            ? window.innerHeight - triggerRect.top + PANEL_GAP_PX
            : triggerRect.bottom + PANEL_GAP_PX,
        maxHeight,
        width,
      });
    };

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);
    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [anchorRef, open]);

  const toggleAllSources = () => {
    onDataSourcesChange(allSourcesSelected ? [] : [...availableDataSources]);
  };

  const toggleSource = (source: FinsterQueryDataSource) => {
    onDataSourcesChange(
      selectedSourceSet.has(source)
        ? selectedDataSources.filter((item) => item !== source)
        : [...selectedDataSources, source]
    );
  };

  const toggleGroup = (sources: readonly FinsterQueryDataSource[]) => {
    const allGroupSourcesSelected = sources.every((source) => selectedSourceSet.has(source));
    onDataSourcesChange(
      allGroupSourcesSelected
        ? selectedDataSources.filter((source) => !sources.includes(source))
        : [...selectedDataSources.filter((source) => !sources.includes(source)), ...sources]
    );
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Data sources"
      className="fixed z-[2147483647] overflow-y-auto rounded-xl border border-border bg-background text-foreground shadow-xl"
      style={{
        left: panelPosition.left,
        width: panelPosition.width,
        maxHeight: panelPosition.maxHeight,
        ...(panelPosition.side === "top"
          ? { bottom: panelPosition.verticalOffset }
          : { top: panelPosition.verticalOffset }),
      }}
    >
      <div className="sticky top-0 z-10 flex items-center gap-2 bg-background px-3 pb-3 pt-3">
        <Button
          ref={backButtonRef}
          type="button"
          variant="outline"
          size="icon"
          aria-label="Back to attachment options"
          onClick={() => onOpenChange(false, "back")}
          className="h-8 w-8 shrink-0 cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <p className="m-0 min-w-0 flex-1 text-sm font-semibold">Data sources</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={toggleAllSources}
          className="h-8 cursor-pointer rounded-md px-3 text-xs hover:border-primary hover:bg-primary/5 hover:text-foreground"
        >
          {allSourcesSelected ? "Deselect All" : "Select All"}
        </Button>
      </div>

      <div className="flex flex-col gap-2 px-3 pb-3">
        {availableGroups.map((group) => {
          const groupSources = group.children.map((child) => child.param);
          const selectedInGroup = groupSources.filter((source) => selectedSourceSet.has(source));
          const groupChecked = selectedInGroup.length === groupSources.length;
          const groupPartial =
            selectedInGroup.length > 0 && selectedInGroup.length < groupSources.length;

          return (
            <div key={group.label}>
              <button
                type="button"
                onClick={() => toggleGroup(groupSources)}
                className="flex w-full cursor-pointer items-start gap-2 rounded-md border-0 bg-transparent px-1 py-1.5 text-left text-sm font-medium text-foreground hover:bg-accent"
              >
                <span
                  className={cn(
                    "mt-0.5 flex size-4 min-h-4 min-w-4 flex-none items-center justify-center rounded border border-border text-primary",
                    (groupChecked || groupPartial) && "border-primary bg-primary-muted"
                  )}
                  aria-hidden
                >
                  {groupChecked ? (
                    <Check className="h-3 w-3" />
                  ) : groupPartial ? (
                    <span className="h-0.5 w-2 rounded bg-primary" />
                  ) : null}
                </span>
                <span>{group.label}</span>
              </button>
              <div className="ml-3 border-l border-border pl-3">
                {group.children.map((source) => {
                  const checked = selectedSourceSet.has(source.param);
                  return (
                    <button
                      key={source.param}
                      type="button"
                      onClick={() => toggleSource(source.param)}
                      className="flex w-full cursor-pointer items-start gap-2 rounded-md border-0 bg-transparent px-1 py-1 text-left text-xs text-foreground hover:bg-accent"
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex size-4 min-h-4 min-w-4 flex-none items-center justify-center rounded border border-border text-primary",
                          checked && "border-primary bg-primary-muted"
                        )}
                        aria-hidden
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span>{source.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
};
